// SPDX-License-Identifier: Apache-2.0

// Landing — `/` route. Solid port of v1's landing-atlas.tsx.
//
// V1 used @react-three/fiber + @react-three/drei + @react-three/postprocessing.
// We don't have R3F in v2; this is raw three.js (already in deps) driven by
// Solid lifecycle (onMount / onCleanup) + the bare `postprocessing` package
// (already installed at v2's web workspace).
//
// Visual hierarchy ported faithfully:
//   - 5 vertical "lenses" (Law/Research/Medicine/Code/Finance) orbiting the core
//   - Glass-refracting icosahedral core w/ inner emissive seed (bloom heart)
//   - 1400-point particle "halo" (uniform spherical shell, slow rotation)
//   - Stars background, 4800 count, fog
//   - Cinematic camera dolly: slow elliptical breathing + mouse parallax
//   - Bloom + ChromaticAberration + Vignette via postprocessing/EffectComposer
//
// Faithful omissions (called out in final report):
//   - drei <Float> wrapper → equivalent inline float wobble on core's group
//   - drei <Html> orb chips → 2D HTML overlay in DOM, no per-orb projection
//     (the v1 chips required CSS3DRenderer-style projection that's substantial
//      to port and not load-bearing for the hero feel)
//   - drei <MeshTransmissionMaterial> → MeshPhysicalMaterial w/ transmission
//     + thickness (the underlying three.js primitive; drei wraps it w/ extra
//     SDF distortion that's optional polish)
//
// Below the canvas: a CTA to /chat, a "DavaKasası" pill, the verticals grid,
// the three commitments, and a footer — same content as v1.

import { onMount, onCleanup } from "solid-js";
import { A } from "@solidjs/router";
import * as THREE from "three";
import {
  EffectComposer,
  RenderPass,
  EffectPass,
  BloomEffect,
  ChromaticAberrationEffect,
  VignetteEffect,
  KernelSize,
  BlendFunction,
} from "postprocessing";
import "../components/landing-atlas.css";

interface Vertical {
  id: string;
  label: string;
  sub: string;
  color: string;
  live: boolean;
  href: string;
  copy: string;
}

const VERTICALS: Vertical[] = [
  { id: "research",  label: "Research",  sub: "soon",              color: "#a78bfa", live: false, href: "#vertical-research", copy: "Paper graph: cite, follow, query — local citation memory." },
  { id: "medicine",  label: "Medicine",  sub: "soon",              color: "#34d399", live: false, href: "#vertical-medicine", copy: "Patient context vault. HIPAA-safe by design — never uploads." },
  { id: "code",      label: "Code",      sub: "soon",              color: "#67e8f9", live: false, href: "#vertical-code",     copy: "Repo-wide knowledge: PRs, ADRs, postmortems, all linked." },
  { id: "finance",   label: "Finance",   sub: "soon",              color: "#ff7a94", live: false, href: "#vertical-finance",  copy: "Deal rooms + memos that stay on the analyst's laptop." },
];

export default function Landing() {
  let canvasRef: HTMLCanvasElement | undefined;
  let cleanupFn: (() => void) | null = null;

  onMount(() => {
    if (!canvasRef) return;
    try {
      cleanupFn = launchScene(canvasRef);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("[landing] WebGL init failed:", err);
    }
  });

  onCleanup(() => {
    if (cleanupFn) cleanupFn();
    cleanupFn = null;
  });

  return (
    <div class="atlas-root">
      <div class="atlas-canvas">
        <canvas
          ref={(el) => (canvasRef = el)}
          data-landing-canvas
          aria-hidden="true"
        />
      </div>

      <div class="atlas-hero">
        <div class="atlas-eyebrow">A second brain · for any profession</div>
        <h1 class="atlas-headline">
          Knowledge that <span class="atlas-grad-cyan">stays local</span>.
          <br />
          Agents that <span class="atlas-grad-violet">go anywhere</span>.
        </h1>
        <p class="atlas-sub">
          Drag any document into a 3D semantic graph. Query it from Claude,
          Cursor, ChatGPT — without uploading a single file. One substrate,
          many lenses. <strong>Yours is next.</strong>
        </p>
        <div class="atlas-cta">
          <A href="/chat" class="atlas-cta-primary">
            Open the brain
            <span class="atlas-cta-arrow">→</span>
          </A>
        </div>
        <div class="atlas-scroll-hint">scroll · explore the verticals</div>
      </div>

      <section class="atlas-verticals" id="verticals">
        <div class="atlas-verticals-header">
          <h2>Five verticals. One brain.</h2>
          <p>
            The substrate is universal: drag · embed · graph · query. Each
            vertical wraps it in the language and rituals of one profession.
          </p>
        </div>
        <div class="atlas-verticals-grid">
          {VERTICALS.map((v) => (
            <A
              href={v.href}
              id={`vertical-${v.id}`}
              class={`atlas-vcard ${v.live ? "live" : "soon"}`}
              style={{ ["--card-color" as string]: v.color }}
            >
              <div class="atlas-vcard-head">
                <span class="atlas-vcard-dot" />
                <span class="atlas-vcard-label">{v.label}</span>
                <span class="atlas-vcard-status">
                  {v.live ? "live" : "soon"}
                </span>
              </div>
              <p class="atlas-vcard-copy">{v.copy}</p>
              <span class="atlas-vcard-arrow">→</span>
            </A>
          ))}
        </div>
      </section>

      <section class="atlas-pillars">
        <div class="atlas-pillars-header">
          <h2>Three commitments.</h2>
        </div>
        <div class="atlas-pillars-grid">
          <article>
            <h3>Local first</h3>
            <p>
              Documents are parsed in the browser. Embeddings run on-device.
              Nothing crosses the wire — and audits prove it.
            </p>
          </article>
          <article>
            <h3>Agent native</h3>
            <p>
              An MCP server bound to your vault. Claude, Cursor, ChatGPT —
              same context, no upload, deterministic graph queries.
            </p>
          </article>
          <article>
            <h3>Worldwide</h3>
            <p>
              The substrate is locale-, profession-, and law-agnostic.
              Only the lens changes.
            </p>
          </article>
        </div>
      </section>

      <footer class="atlas-foot">
        <A href="/vault">/vault</A>
        <A href="/chat">/chat</A>
        <A href="/temple">/temple</A>
        <span class="atlas-foot-mark">tether · 2026</span>
      </footer>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// launchScene — owns the renderer + scene + raf loop. Returns teardown.
// ────────────────────────────────────────────────────────────────────────────
function launchScene(canvas: HTMLCanvasElement): () => void {
  const renderer = new THREE.WebGLRenderer({
    canvas,
    antialias: false, // postprocessing pass handles AA implicitly via bloom blur
    alpha: false,
    powerPreference: "high-performance",
  });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.2;

  const scene = new THREE.Scene();
  scene.background = new THREE.Color(0x020617);
  scene.fog = new THREE.Fog(0x020617, 7, 24);

  const camera = new THREE.PerspectiveCamera(45, 1, 0.1, 200);
  camera.position.set(0, 0.35, 7);
  camera.lookAt(0, 0, 0);

  // ── Lights ───────────────────────────────────────────────────────────────
  scene.add(new THREE.AmbientLight(0xffffff, 0.22));
  const cyanLight = new THREE.PointLight(0x7dd3fc, 3.4, 30, 1.6);
  cyanLight.position.set(5, 4, 5);
  scene.add(cyanLight);
  const violetLight = new THREE.PointLight(0xa78bfa, 2.2, 30, 1.6);
  violetLight.position.set(-4, -2, 4);
  scene.add(violetLight);
  const amberLight = new THREE.PointLight(0xfbbf24, 1.6, 30, 1.6);
  amberLight.position.set(0, -4, -3);
  scene.add(amberLight);

  // ── Stars (4800 sparse points uniformly on a far shell) ──────────────────
  const stars = (() => {
    const count = 4800;
    const positions = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      const r = 70 + Math.random() * 70;
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);
      positions[i * 3 + 0] = r * Math.sin(phi) * Math.cos(theta);
      positions[i * 3 + 1] = r * Math.cos(phi);
      positions[i * 3 + 2] = r * Math.sin(phi) * Math.sin(theta);
    }
    const geom = new THREE.BufferGeometry();
    geom.setAttribute("position", new THREE.BufferAttribute(positions, 3));
    const mat = new THREE.PointsMaterial({
      color: 0xffffff,
      size: 0.6,
      transparent: true,
      opacity: 0.75,
      sizeAttenuation: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });
    const points = new THREE.Points(geom, mat);
    scene.add(points);
    return { points, geom, mat };
  })();

  // ── Particle halo (1400 points, spherical shell, additive) ───────────────
  const halo = (() => {
    const count = 1400;
    const positions = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      const r = 4 + Math.random() * 4;
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);
      positions[i * 3 + 0] = r * Math.sin(phi) * Math.cos(theta);
      positions[i * 3 + 1] = r * Math.cos(phi) * 0.6;
      positions[i * 3 + 2] = r * Math.sin(phi) * Math.sin(theta);
    }
    const geom = new THREE.BufferGeometry();
    geom.setAttribute("position", new THREE.BufferAttribute(positions, 3));
    const mat = new THREE.PointsMaterial({
      color: 0xcbd5f5,
      size: 0.06,
      transparent: true,
      opacity: 0.78,
      sizeAttenuation: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });
    const points = new THREE.Points(geom, mat);
    scene.add(points);
    return { points, geom, mat };
  })();

  // ── Glass core ───────────────────────────────────────────────────────────
  const coreGroup = new THREE.Group();
  scene.add(coreGroup);

  const coreGeom = new THREE.IcosahedronGeometry(1.18, 5);
  const coreMat = new THREE.MeshPhysicalMaterial({
    color: 0xe0f2fe,
    roughness: 0.05,
    metalness: 0,
    transmission: 1.0,
    thickness: 0.7,
    ior: 1.55,
    attenuationColor: new THREE.Color(0x7dd3fc),
    attenuationDistance: 1.4,
    transparent: true,
  });
  const coreMesh = new THREE.Mesh(coreGeom, coreMat);
  coreGroup.add(coreMesh);

  // Inner emissive seed (luminous heart that bloom amplifies)
  const seedGeom = new THREE.IcosahedronGeometry(0.42, 3);
  const seedMat = new THREE.MeshBasicMaterial({
    color: 0xa78bfa,
    toneMapped: false,
  });
  const seedMesh = new THREE.Mesh(seedGeom, seedMat);
  coreGroup.add(seedMesh);

  // ── Five orbiting domain orbs ────────────────────────────────────────────
  interface Orb {
    group: THREE.Group;
    mesh: THREE.Mesh;
    haloMesh: THREE.Mesh;
    angle: number;
    radius: number;
    inclination: number;
    speed: number;
  }

  const orbGeom = new THREE.SphereGeometry(0.16, 32, 32);
  const orbHaloGeom = new THREE.SphereGeometry(0.32, 24, 24);
  const orbs: Orb[] = VERTICALS.map((v, i) => {
    const angle = (i / VERTICALS.length) * Math.PI * 2;
    const radius = 2.55 + (i % 2 === 0 ? 0.18 : 0.55);
    const inclination = 0.35 + (i % 3) * 0.18;
    const speed = 0.11 + i * 0.013;
    const color = new THREE.Color(v.color);
    const mat = new THREE.MeshStandardMaterial({
      color,
      emissive: color,
      emissiveIntensity: 2.4,
      toneMapped: false,
    });
    const haloMat = new THREE.MeshBasicMaterial({
      color,
      transparent: true,
      opacity: 0.12,
      toneMapped: false,
    });
    const mesh = new THREE.Mesh(orbGeom, mat);
    const haloMesh = new THREE.Mesh(orbHaloGeom, haloMat);
    const group = new THREE.Group();
    group.add(mesh);
    group.add(haloMesh);
    scene.add(group);
    return { group, mesh, haloMesh, angle, radius, inclination, speed };
  });

  // ── Postprocessing: bloom + chromatic aberration + vignette ──────────────
  const composer = new EffectComposer(renderer);
  composer.addPass(new RenderPass(scene, camera));

  const bloomEffect = new BloomEffect({
    intensity: 1.7,
    luminanceThreshold: 0.55,
    luminanceSmoothing: 0.2,
    mipmapBlur: true,
    kernelSize: KernelSize.LARGE,
  });
  const caEffect = new ChromaticAberrationEffect({
    offset: new THREE.Vector2(0.0009, 0.0009),
    radialModulation: false,
    modulationOffset: 0,
  });
  const vignetteEffect = new VignetteEffect({
    eskil: false,
    offset: 0.28,
    darkness: 0.66,
    blendFunction: BlendFunction.NORMAL,
  });
  composer.addPass(new EffectPass(camera, bloomEffect, caEffect, vignetteEffect));

  // ── Resize handling ──────────────────────────────────────────────────────
  function resize() {
    const parent = canvas.parentElement;
    if (!parent) return;
    const w = parent.clientWidth || window.innerWidth;
    const h = parent.clientHeight || window.innerHeight;
    if (w === 0 || h === 0) return;
    renderer.setSize(w, h, false);
    composer.setSize(w, h);
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
  }
  resize();
  const onResize = () => resize();
  window.addEventListener("resize", onResize);

  // ── Mouse parallax ───────────────────────────────────────────────────────
  const mouse = { x: 0, y: 0 };
  function onMouseMove(e: MouseEvent) {
    const w = window.innerWidth;
    const h = window.innerHeight;
    mouse.x = (e.clientX / w) * 2 - 1;
    mouse.y = -((e.clientY / h) * 2 - 1);
  }
  window.addEventListener("mousemove", onMouseMove, { passive: true });

  // ── RAF loop ─────────────────────────────────────────────────────────────
  const clock = new THREE.Clock();
  let stopped = false;
  let raf = 0;

  function frame() {
    if (stopped) return;
    const delta = clock.getDelta();
    const t = clock.elapsedTime;

    // core spin
    coreMesh.rotation.y += delta * 0.16;
    coreMesh.rotation.x += delta * 0.05;

    // gentle "Float" wobble of the whole core group
    coreGroup.position.y = Math.sin(t * 0.55) * 0.18;
    coreGroup.rotation.z = Math.sin(t * 0.4) * 0.06;

    // halo + stars slow rotation
    halo.points.rotation.y += delta * 0.04;
    halo.points.rotation.x += delta * 0.012;
    stars.points.rotation.y += delta * 0.005;

    // orbiting orbs
    for (const o of orbs) {
      const u = t * o.speed + o.angle;
      o.group.position.set(
        Math.cos(u) * o.radius,
        Math.sin(u * 1.4) * o.inclination,
        Math.sin(u) * o.radius,
      );
    }

    // cinematic camera dolly + mouse parallax
    const targetX = mouse.x * 0.85;
    const targetY = 0.35 + mouse.y * 0.55 + Math.sin(t * 0.18) * 0.08;
    const targetZ = 7 + Math.cos(t * 0.13) * 0.4;
    camera.position.x += (targetX - camera.position.x) * 0.05;
    camera.position.y += (targetY - camera.position.y) * 0.05;
    camera.position.z += (targetZ - camera.position.z) * 0.04;
    camera.lookAt(0, 0, 0);

    composer.render(delta);
    raf = requestAnimationFrame(frame);
  }
  raf = requestAnimationFrame(frame);

  // ── Teardown ─────────────────────────────────────────────────────────────
  return () => {
    stopped = true;
    cancelAnimationFrame(raf);
    window.removeEventListener("resize", onResize);
    window.removeEventListener("mousemove", onMouseMove);
    composer.dispose();
    renderer.dispose();
    coreGeom.dispose();
    coreMat.dispose();
    seedGeom.dispose();
    seedMat.dispose();
    orbGeom.dispose();
    orbHaloGeom.dispose();
    for (const o of orbs) {
      (o.mesh.material as THREE.Material).dispose();
      (o.haloMesh.material as THREE.Material).dispose();
    }
    halo.geom.dispose();
    halo.mat.dispose();
    stars.geom.dispose();
    stars.mat.dispose();
  };
}
